import { storePath, storeProductPath } from '../../lib/storePath';
import WeightInfo from '../../components/WeightInfo';
import CategoryVisitNotice from '../../components/CategoryVisitNotice';
import BranchVisitNotice from '../../components/BranchVisitNotice';
import React, { useEffect, useMemo, useRef, useState } from "react";

import { useParams, useNavigate, useLocation } from "react-router-dom";

import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import ProductImageGallery from "../../components/ProductImageGallery";
import Seo from "../../components/Seo";

import {
  Star,
  Bike,
  Clock,
  Info,
  ShoppingCart,
  Plus,
  Minus,
  ArrowLeft,
  ChevronDown,
  Store,
  Check,
} from "lucide-react";

import { get, post } from "../../lib/api";

const ProductDetail = () => {
  const { vendorSlug: vendorSlugParam, productSlug } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  // ============================================================
  // PRODUCT / VENDOR
  // ============================================================

  const [product, setProduct] = useState(null);
  const [vendor, setVendor] = useState(null);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // ============================================================
  // CART / QUANTITY
  // ============================================================

  const [quantity, setQuantity] = useState(1);
  const [cartMessage, setCartMessage] = useState("");
  const [isAddingToCart, setIsAddingToCart] = useState(false);

  // ============================================================
  // VARIANTS
  // ============================================================

  const [selectedVariant, setSelectedVariant] = useState(null);
  const [showVariants, setShowVariants] = useState(false);
  const [variantToast, setVariantToast] = useState("");

  // ============================================================
  // REFS
  // ============================================================

  const variantToastTimerRef = useRef(null);
  const cartMessageTimerRef = useRef(null);

  // ============================================================
  // CLEANUP TIMERS
  // ============================================================

  useEffect(() => {
    return () => {
      if (variantToastTimerRef.current) {
        clearTimeout(variantToastTimerRef.current);
      }

      if (cartMessageTimerRef.current) {
        clearTimeout(cartMessageTimerRef.current);
      }
    };
  }, []);

  // ============================================================
  // FETCH PRODUCT + VENDOR
  // ============================================================

  useEffect(() => {
    let isMounted = true;

    const fetchData = async () => {
      if (!productSlug) {
        if (isMounted) {
          setError("Product address is missing.");
          setLoading(false);
        }

        return;
      }

      try {
        if (isMounted) {
          setLoading(true);
          setError("");

          setProduct(null);
          setVendor(null);

          setSelectedVariant(null);
          setQuantity(1);
          setShowVariants(false);
        }

        // --------------------------------------------------------
        // FETCH PRODUCT
        // --------------------------------------------------------

        const productResponse = await get(`/products/${productSlug}`);

        if (!isMounted) {
          return;
        }

        if (!productResponse?.success || !productResponse?.product) {
          setError(
            productResponse?.message || "Failed to fetch product details."
          );

          return;
        }

        const fetchedProduct = productResponse.product;

        setProduct(fetchedProduct);

        if (fetchedProduct?.store) {
          setVendor(fetchedProduct.store);
          return;
        }

        // --------------------------------------------------------
        // FETCH VENDOR
        // --------------------------------------------------------

        const rawVendorId = fetchedProduct?.vendorId;

        const vendorId =
          rawVendorId && typeof rawVendorId === "object"
            ? rawVendorId?._id || rawVendorId?.id
            : rawVendorId;

        if (!vendorId) {
          setVendor(null);
          return;
        }

        try {
          const vendorResponse = await get(`/vendors/${vendorId}`);

          if (!isMounted) {
            return;
          }

          if (vendorResponse?.success && vendorResponse?.vendor) {
            setVendor(vendorResponse.vendor);
          } else {
            setVendor(null);
          }
        } catch (vendorError) {
          console.error("Error fetching vendor:", vendorError);

          if (isMounted) {
            setVendor(null);
          }
        }
      } catch (err) {
        if (!isMounted) {
          return;
        }

        console.error("Error fetching product:", err);

        setError(
          err?.response?.data?.message ||
            err?.message ||
            "An error occurred while fetching product details."
        );
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchData();

    return () => {
      isMounted = false;
    };
  }, [productSlug]);
  const vendorSlug = product?.vendorId?.slug || vendor?.slug || null;
  const store = { ...(typeof product?.vendorId === 'object' ? product.vendorId : {}), ...product?.store, ...vendor, slug: vendorSlug };
  const productPath = storeProductPath(store, product?.slug);
  const fromVendorStore = location.state?.fromVendorStore === true && location.state?.vendorSlug === vendorSlug;

  useEffect(() => {
    if (
      product?.slug &&
      vendorSlug &&
      (productSlug !== product.slug || vendorSlugParam !== vendorSlug || (vendor?.branch && location.pathname !== productPath))
    ) {
      navigate(`${productPath}${location.search}${location.hash}`, {
        replace: true,
        state: location.state,
      });
    }
  }, [navigate, product?.slug, productSlug, vendorSlug, vendorSlugParam, location.state, location.pathname, location.search, location.hash, productPath, vendor?.branch]);

  const handleVisitStore = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!vendorSlug) {
      showCartMessage("Store information is unavailable.");
      return;
    }
    navigate(storePath(store));
  };
  // ============================================================
  // NORMALIZED VARIANTS
  // ============================================================

  const variants = useMemo(() => {
    if (!Array.isArray(product?.variants)) {
      return [];
    }

    return product.variants
      .filter((variant) => {
        if (!variant) {
          return false;
        }

        return Boolean(String(variant.variantName || "").trim());
      })
      .map((variant) => {
        const originalPrice = Number(variant.originalPrice);

        const discountedPrice =
          variant.discountedPrice !== undefined &&
          variant.discountedPrice !== null &&
          variant.discountedPrice !== ""
            ? Number(variant.discountedPrice)
            : null;

        return {
          ...variant,

          variantName: String(variant.variantName).trim(),

          originalPrice:
            Number.isFinite(originalPrice) && originalPrice >= 0
              ? originalPrice
              : 0,

          discountedPrice:
            Number.isFinite(discountedPrice) && discountedPrice >= 0
              ? discountedPrice
              : null,
        };
      });
  }, [product?.variants]);

  // ============================================================
  // DEFAULT VARIANT
  // ============================================================

  const defaultVariant = useMemo(() => {
    if (variants.length === 0) {
      return null;
    }

    return (
      variants.find((variant) => variant.isDefault === true) || variants[0]
    );
  }, [variants]);

  // ============================================================
  // VARIANT KEY
  // ============================================================

  const getVariantKey = (variant) => {
    if (!variant) {
      return "";
    }

    return String(variant._id || variant.id || variant.variantName || "");
  };

  // ============================================================
  // INITIALIZE / VALIDATE SELECTED VARIANT
  //
  // This effect runs when the product or its variants change.
  //
  // Important:
  // It does NOT blindly overwrite a user's selection.
  // ============================================================

  useEffect(() => {
    if (!product) {
      return;
    }

    // No variants
    if (variants.length === 0) {
      setSelectedVariant(null);
      setQuantity(1);
      setShowVariants(false);

      return;
    }

    // Product has variants.
    // If there is no selected variant yet,
    // select the default variant.
    if (!selectedVariant) {
      setSelectedVariant(defaultVariant || null);
      setQuantity(1);

      return;
    }

    // Make sure the currently selected variant
    // still exists in the current product.
    const selectedKey = getVariantKey(selectedVariant);

    const stillExists = variants.some(
      (variant) => getVariantKey(variant) === selectedKey
    );

    if (!stillExists) {
      setSelectedVariant(defaultVariant || null);
      setQuantity(1);
    }
  }, [product, variants, defaultVariant, selectedVariant]);

  // ============================================================
  // EFFECTIVE VARIANT
  // ============================================================

  const effectiveVariant = selectedVariant || defaultVariant || null;
  const variantDescription = effectiveVariant?.description ?? product?.description ?? '';

  // ============================================================
  // VARIANT TOAST
  // ============================================================

  const showVariantToast = (message) => {
    if (variantToastTimerRef.current) {
      clearTimeout(variantToastTimerRef.current);
    }

    setVariantToast(message);

    variantToastTimerRef.current = setTimeout(() => {
      setVariantToast("");

      variantToastTimerRef.current = null;
    }, 2500);
  };

  // ============================================================
  // TOGGLE VARIANTS
  // ============================================================

  const toggleVariants = () => {
    if (isAddingToCart) {
      return;
    }

    if (variants.length <= 1) {
      showVariantToast(
        variants.length === 1
          ? `Only ${variants[0].variantName} is available for this product.`
          : "No product variants are available."
      );

      return;
    }

    setShowVariants((previous) => !previous);
  };

  // ============================================================
  // CHANGE VARIANT
  // ============================================================

  const handleVariantChange = (variant) => {
    if (isAddingToCart || !variant) {
      return;
    }

    setSelectedVariant(variant);
    setQuantity(1);
    setShowVariants(false);
  };

  // ============================================================
  // PRICE
  // ============================================================

  const originalPrice = useMemo(() => {
    const value = effectiveVariant
      ? Number(effectiveVariant.originalPrice)
      : Number(product?.originalPrice);

    if (Number.isFinite(value) && value >= 0) {
      return value;
    }

    return 0;
  }, [effectiveVariant, product?.originalPrice]);

  const discountedPrice = useMemo(() => {
    if (!effectiveVariant) {
      return null;
    }

    const value = Number(effectiveVariant.discountedPrice);

    if (Number.isFinite(value) && value >= 0) {
      return value;
    }

    return null;
  }, [effectiveVariant]);

  const hasDiscount =
    discountedPrice !== null && discountedPrice < originalPrice;

  const currentPrice = hasDiscount ? discountedPrice : originalPrice;

  const discountPercentage =
    hasDiscount && originalPrice > 0
      ? Math.round(((originalPrice - discountedPrice) / originalPrice) * 100)
      : 0;

  const savings = hasDiscount
    ? Math.max(0, originalPrice - discountedPrice)
    : 0;

  const isPriceValid =
    Number.isFinite(Number(currentPrice)) && Number(currentPrice) > 0;

  // ============================================================
  // STOCK
  // ============================================================

  const stockAvailable = useMemo(() => {
    const value = Number(effectiveVariant ? effectiveVariant.stockAvailable || 0 : product?.stockAvailable);

    if (Number.isFinite(value) && value >= 0) {
      return Math.floor(value);
    }

    return null;
  }, [product?.stockAvailable, effectiveVariant]);

  const isOutOfStock = stockAvailable !== null && stockAvailable <= 0;

  const storeClosed = store.isOpen === false || store.adminClosed === true;
  const isProductHold = Boolean(product?.isProductHold || store.productsOnHold);

  const isUnavailable = storeClosed || isProductHold || isOutOfStock;

  const maxQuantity =
    stockAvailable !== null && stockAvailable > 0 ? stockAvailable : 99;

  // ============================================================
  // KEEP QUANTITY WITHIN STOCK LIMIT
  // ============================================================

  useEffect(() => {
    setQuantity((current) => {
      const numericValue = Number(current);

      const normalizedCurrent = Number.isFinite(numericValue)
        ? Math.floor(numericValue)
        : 1;

      const nextQuantity = Math.min(
        maxQuantity,
        Math.max(1, normalizedCurrent)
      );

      if (nextQuantity === current) {
        return current;
      }

      return nextQuantity;
    });
  }, [maxQuantity]);

  // ============================================================
  // QUANTITY
  // ============================================================

  const handleIncreaseQuantity = () => {
    if (vendor?.isOpen === false || isAddingToCart || isUnavailable) {
      return;
    }

    setQuantity((current) => {
      const numericValue = Number(current);

      const safeCurrent = Number.isFinite(numericValue)
        ? Math.floor(numericValue)
        : 1;

      return Math.min(maxQuantity, Math.max(1, safeCurrent + 1));
    });
  };

  const handleDecreaseQuantity = () => {
    if (isAddingToCart || isUnavailable) {
      return;
    }

    setQuantity((current) => {
      const numericValue = Number(current);

      const safeCurrent = Number.isFinite(numericValue)
        ? Math.floor(numericValue)
        : 1;

      return Math.max(1, safeCurrent - 1);
    });
  };

  const handleQuantityInput = (event) => {
    const rawValue = event.target.value;

    if (rawValue === "") {
      setQuantity(1);
      return;
    }

    const value = Number(rawValue);

    if (!Number.isFinite(value)) {
      return;
    }

    setQuantity(Math.min(maxQuantity, Math.max(1, Math.floor(value))));
  };

  // ============================================================
  // CART MESSAGE
  // ============================================================

  const showCartMessage = (message) => {
    if (cartMessageTimerRef.current) {
      clearTimeout(cartMessageTimerRef.current);
    }

    setCartMessage(message);

    cartMessageTimerRef.current = setTimeout(() => {
      setCartMessage("");

      cartMessageTimerRef.current = null;
    }, 3000);
  };

  // ============================================================
  // RESOLVE PRODUCT ID
  // ============================================================

  const resolvedProductId = product?._id || product?.id || "";

  // ============================================================
  // RESOLVE VENDOR ID
  // ============================================================

  const resolvedVendorId = useMemo(() => {
    const vendorValue = vendor?._id || vendor?.id;

    if (vendorValue) {
      return String(vendorValue);
    }

    const productVendor = product?.vendorId;

    if (productVendor && typeof productVendor === "object") {
      return String(productVendor?._id || productVendor?.id || "");
    }

    return productVendor ? String(productVendor) : "";
  }, [vendor, product?.vendorId]);

  // ============================================================
  // ADD TO CART
  // ============================================================

  const handleAddToCart = async () => {
    if (isAddingToCart || isUnavailable) {
      return;
    }

    // ----------------------------------------------------------
    // Resolve variant
    // ----------------------------------------------------------

    const variantToUse = effectiveVariant;

    // ----------------------------------------------------------
    // Validate variant
    // ----------------------------------------------------------

    if (variants.length > 0 && !variantToUse) {
      showCartMessage("Please select a product variant.");

      return;
    }

    // ----------------------------------------------------------
    // Validate product ID
    // ----------------------------------------------------------

    if (!resolvedProductId) {
      showCartMessage("This product is currently unavailable.");

      return;
    }

    // ----------------------------------------------------------
    // Validate vendor
    // ----------------------------------------------------------

    if (!resolvedVendorId) {
      showCartMessage("Vendor information is missing. Cannot add to cart.");

      return;
    }

    // ----------------------------------------------------------
    // Validate price
    // ----------------------------------------------------------

    const price = Number(currentPrice);

    if (!Number.isFinite(price) || price <= 0) {
      showCartMessage("This product does not have a valid price.");

      return;
    }

    // ----------------------------------------------------------
    // Validate quantity
    // ----------------------------------------------------------

    const normalizedQuantity = Math.floor(Number(quantity));

    if (!Number.isFinite(normalizedQuantity) || normalizedQuantity < 1) {
      showCartMessage("Please enter a valid quantity.");

      return;
    }

    // ----------------------------------------------------------
    // Validate stock
    // ----------------------------------------------------------

    if (stockAvailable !== null && normalizedQuantity > stockAvailable) {
      showCartMessage(`Only ${stockAvailable} item(s) are available.`);

      setQuantity(Math.max(1, stockAvailable));

      return;
    }

    // ----------------------------------------------------------
    // Product display name
    // ----------------------------------------------------------

    const variantName = variantToUse
      ? String(variantToUse.variantName || "").trim()
      : "";

    const baseProductName = String(product?.name || "Unnamed Product").trim();

    const displayProductName = variantName
      ? `${baseProductName} - ${variantName}`
      : baseProductName;

    // ----------------------------------------------------------
    // Cart payload
    // ----------------------------------------------------------

    const cartPayload = {
      productId: String(resolvedProductId),
      variantId: variantToUse?._id,
      variantName,

      vendorId: String(resolvedVendorId),

      name: displayProductName,

      price,

      quantity: normalizedQuantity,
    };

    // ----------------------------------------------------------
    // Final validation
    // ----------------------------------------------------------

    if (
      !cartPayload.productId ||
      !cartPayload.vendorId ||
      !cartPayload.name ||
      !Number.isFinite(cartPayload.price) ||
      cartPayload.price <= 0 ||
      !Number.isInteger(cartPayload.quantity) ||
      cartPayload.quantity < 1
    ) {
      console.error("Invalid cart payload:", cartPayload);

      showCartMessage("Invalid product data. Please try again.");

      return;
    }

    // ----------------------------------------------------------
    // Send request
    // ----------------------------------------------------------

    try {
      setIsAddingToCart(true);
      setCartMessage("");

      console.log("Adding to cart with payload:", cartPayload);

      const response = await post("/cart/items", cartPayload);

      if (response?.success) {
        showCartMessage(`✓ Added ${normalizedQuantity} item(s) to cart!`);

        setQuantity(1);
      } else {
        showCartMessage(response?.message || "Failed to add to cart.");
      }
    } catch (err) {
      console.error("Add to cart error:", err);

      showCartMessage(
        err?.response?.data?.message || err?.message || "Error adding to cart."
      );
    } finally {
      setIsAddingToCart(false);
    }
  };

  // ============================================================
  // LOADING
  // ============================================================

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Seo title="Loading Product | BigCircle" robots="noindex, follow" />
        <div className="text-center">
          <div className="text-lg font-semibold mb-2">
            Loading product details...
          </div>

          <div className="text-gray-500">Please wait</div>
        </div>
      </div>
    );
  }

  // ============================================================
  // ERROR
  // ============================================================

  if (error || !product) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen gap-4">
        <Seo
          title="Product Not Found | BigCircle"
          description="The requested BigCircle product could not be found."
          robots="noindex, follow"
        />
        <div className="text-center">
          <div className="text-lg font-semibold text-red-600 mb-2">
            {error || "Product not found"}
          </div>
        </div>

        <Button
          onClick={() => navigate(-1)}
          variant="outline"
          className="gap-2"
        >
          <ArrowLeft className="w-4 h-4" />
          Go Back
        </Button>
      </div>
    );
  }

  // ============================================================
  // RENDER
  // ============================================================

  return (
    <div className="mx-auto max-w-4xl space-y-3"><BranchVisitNotice vendor={vendor} /><CategoryVisitNotice vendor={vendor} />
      <Seo
        title={`${product.name} | ${vendor?.storeName || "BigCircle"}`}
        description={
          product.description ||
          `Order ${product.name} from ${
            vendor?.storeName || "a local store"
          } on BigCircle.`
        }
        path={productPath}
        image={product.images?.[0] || vendor?.logo}
        imageAlt={product.name}
        type="product"
        structuredData={{
          "@context": "https://schema.org",
          "@type": "Product",
          name: product.name,
          description: product.description || undefined,
          image: product.images || undefined,
          category: Array.isArray(product.productTags)
            ? product.productTags.join(", ")
            : product.category || undefined,
          brand: vendor?.storeName
            ? { "@type": "Brand", name: vendor.storeName }
            : undefined,
          offers: {
            "@type": "Offer",
            priceCurrency: "PHP",
            price: Number(currentPrice).toFixed(2),
            availability: isUnavailable
              ? "https://schema.org/OutOfStock"
              : "https://schema.org/InStock",
            url: `${String(
              process.env.REACT_APP_SITE_URL || window.location.origin
            ).replace(/\/$/, "")}${productPath}`,
          },
        }}
      />
      {/* ======================================================
          HEADER
      ====================================================== */}

      <header className="flex items-center gap-2">
        <Button
          type="button"
          onClick={() => navigate(-1)}
          variant="ghost"
          size="icon"
          aria-label="Go back"
        >
          <ArrowLeft className="h-5 w-5" aria-hidden="true" />
        </Button>

        <h1 className="text-lg font-semibold">Product Details</h1>
      </header>

      {/* ======================================================
          VARIANT TOAST
      ====================================================== */}

      {variantToast && (
        <div className="fixed top-5 left-1/2 -translate-x-1/2 z-[100]">
          <div className="bg-gray-900 text-white text-sm font-medium px-5 py-3 rounded-full shadow-xl">
            {variantToast}
          </div>
        </div>
      )}

      {/* ======================================================
          CART MESSAGE
      ====================================================== */}

      {cartMessage && (
        <div
          className={`
            p-4
            rounded-lg
            text-white
            ${cartMessage.includes("✓") ? "bg-green-500" : "bg-red-500"}
          `}
        >
          {cartMessage}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
        {/* ====================================================
            LEFT: IMAGE GALLERY
        ==================================================== */}

        <div className="lg:col-span-2">
          <ProductImageGallery
            images={Array.isArray(product.images) ? product.images : []}
          />
        </div>

        {/* ====================================================
            RIGHT: PRODUCT INFORMATION
        ==================================================== */}

        <div className="space-y-3">
          {/* ==================================================
              VENDOR
          ================================================== */}

          {vendor && (
            <Card>
              <CardContent className="p-3">
                <div className="flex items-start gap-3">
                  {vendor.logo ? (
                    <img
                      src={vendor.logo}
                      alt={vendor.storeName || "Store"}
                      className="w-12 h-12 rounded-lg object-cover"
                    />
                  ) : (
                    <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center">
                      <Info className="w-5 h-5 text-primary" />
                    </div>
                  )}

                  <div className="flex-grow">
                    <h3 className="font-semibold text-sm mb-1">
                      {vendor.storeName || "Store"}
                    </h3>

                    {vendor.stats &&
                      Number(vendor.stats.ratingCount || 0) > 0 &&
                      vendor.stats.ratingAverage !== undefined && (
                        <div className="flex items-center gap-1 text-xs">
                          <Star className="w-3 h-3 fill-yellow-400 text-yellow-400" />

                          <span>
                            {Number(vendor.stats.ratingAverage).toFixed(1)}
                          </span>
                        </div>
                      )}
                  </div>
                  {!fromVendorStore && (
                  <button
                    type="button"
                    onClick={handleVisitStore}
                    className="flex items-center gap-1 text-xs font-semibold text-primary hover:text-primary/80 whitespace-nowrap transition-colors"
                  >
                    <Store className="w-3.5 h-3.5" />
                    Visit Store
                  </button>
                  )}
                </div>

                <div className="mt-3 space-y-2 text-xs text-gray-600 border-t pt-3">
                  {vendor.storeEtaMinMinutes && vendor.storeEtaMaxMinutes && (
                      <div className="flex items-center gap-2">
                        <Bike className="w-4 h-4" />

                        <span>{vendor.storeEtaMinMinutes}-{vendor.storeEtaMaxMinutes}mins delivery/preparation</span>
                      </div>
                    )}

                  {vendor.isOpen ? (
                    <div className="flex items-center gap-2 text-green-600">
                      <Info className="w-4 h-4" />
                      <span>Store is Open</span>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 text-red-600">
                      <Info className="w-4 h-4" />
                      <span>Store is Closed</span>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          {/* ==================================================
              PRODUCT INFO
          ================================================== */}

          <Card>
            <CardContent className="p-3 space-y-3">
              {/* PRODUCT NAME */}

              <div>
                <h2 className="text-lg font-semibold">{product.name}</h2>
              </div>

             

              {/* =================================================
                  VARIANT SELECTOR
              ================================================= */}

              {variants.length > 0 && (
                <div className="border rounded-lg p-3 bg-gray-50">
                  <div className="flex items-center justify-between gap-3">
                    <button
                      type="button"
                      onClick={toggleVariants}
                      disabled={isAddingToCart}
                      className="flex items-center gap-1.5 text-sm font-semibold text-gray-700 hover:text-primary transition-colors"
                    >
                      <span>{variants.length} variants</span>

                      <ChevronDown
                        className={`
                          w-4 h-4
                          transition-transform
                          duration-200
                          ${showVariants ? "rotate-180" : ""}
                        `}
                      />
                    </button>

                    <button
                      type="button"
                      onClick={toggleVariants}
                      disabled={isAddingToCart}
                      className="flex items-center gap-1.5 max-w-[60%] text-sm font-semibold text-primary hover:text-primary/80 transition-colors"
                    >
                      <span className="truncate">
                        {effectiveVariant?.variantName || "Select"}
                      </span>

                      <ChevronDown
                        className={`
                          w-4 h-4
                          flex-shrink-0
                          transition-transform
                          duration-200
                          ${showVariants ? "rotate-180" : ""}
                        `}
                      />
                    </button>
                  </div>

                  {showVariants && variants.length > 1 && (
                    <div className="mt-3 -mx-1">
                      <div className="flex gap-2 overflow-x-auto px-1 pb-2 snap-x snap-mandatory scrollbar-thin">
                        {variants.map((variant, index) => {
                          const isSelected =
                            getVariantKey(selectedVariant) ===
                            getVariantKey(variant);

                          const variantOriginal =
                            Number(variant.originalPrice) || 0;

                          const variantDiscounted =
                            variant.discountedPrice !== null &&
                            Number.isFinite(Number(variant.discountedPrice))
                              ? Number(variant.discountedPrice)
                              : null;

                          const variantHasDiscount =
                            variantDiscounted !== null &&
                            variantDiscounted < variantOriginal;

                          const variantPrice = variantHasDiscount
                            ? variantDiscounted
                            : variantOriginal;

                          return (
                            <button
                              key={
                                getVariantKey(variant) ||
                                `${variant.variantName}-${index}`
                              }
                              type="button"
                              onClick={() => handleVariantChange(variant)}
                              disabled={isAddingToCart}
                              className={`
                                    relative
                                    flex
                                    items-center
                                    justify-between
                                    gap-3
                                    min-w-[160px]
                                    max-w-[200px]
                                    h-11
                                    flex-shrink-0
                                    snap-start
                                    rounded-lg
                                    border
                                    px-3
                                    transition-all
                                    ${
                                      isSelected
                                        ? "border-primary bg-primary/10 ring-1 ring-primary"
                                        : "border-gray-200 bg-white hover:border-primary/50 hover:bg-white"
                                    }
                                    ${
                                      isAddingToCart
                                        ? "opacity-50 cursor-not-allowed"
                                        : ""
                                    }
                                  `}
                            >
                              <span
                                className={`
                                      text-xs
                                      font-semibold
                                      truncate
                                      ${
                                        isSelected
                                          ? "text-primary"
                                          : "text-gray-700"
                                      }
                                    `}
                              >
                                {variant.variantName}
                              </span>

                              <div className="flex flex-col items-end">
                                {variantHasDiscount && (
                                  <span className="text-[9px] text-gray-400 line-through">
                                    ₱{variantOriginal.toFixed(2)}
                                  </span>
                                )}

                                <span
                                  className={`
                                        text-[11px]
                                        font-semibold
                                        whitespace-nowrap
                                        ${
                                          isSelected
                                            ? "text-primary"
                                            : "text-gray-600"
                                        }
                                      `}
                                >
                                  ₱{Number(variantPrice).toFixed(2)}
                                </span>
                              </div>

                              {isSelected && (
                                <Check className="absolute -top-1.5 -right-1.5 w-4 h-4 rounded-full bg-primary text-white p-0.5" />
                              )}
                            </button>
                          );
                        })}
                      </div>

                      {variants.length > 2 && (
                        <p className="text-[10px] text-gray-400 text-center mt-1">
                          Swipe horizontally to view variants
                        </p>
                      )}
                    </div>
                  )}
                </div>
              )}

               {/* =================================================
                  CATEGORIES
              ================================================= */}

              {Array.isArray(product.categories) &&
                product.categories.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {product.categories.map((category, index) => (
                      <span
                        key={`${String(category)}-${index}`}
                        className="text-xs bg-blue-100 text-blue-700 px-3 py-1 rounded-full font-medium"
                      >
                        {category}
                      </span>
                    ))}
                  </div>
                )}

              {/* =================================================
                  PRICE
              ================================================= */}

              <div className="bg-gray-50 p-3 rounded-lg">
                {effectiveVariant && (
                  <div className="mb-2 flex items-start justify-between gap-3">
                    <span className="inline-flex shrink-0 items-center gap-1.5 bg-primary/10 text-primary px-2.5 py-1 rounded-full text-xs font-semibold">
                      <Check className="w-3 h-3" />

                      {effectiveVariant.variantName}
                    </span>
                    <div className="min-w-0 break-words text-right">
                      <WeightInfo {...effectiveVariant} />
                    </div>
                  </div>
                )}

                <div className="flex items-baseline gap-3 mb-2">
                  {hasDiscount ? (
                    <>
                      <span className="text-3xl font-bold text-red-600">
                        ₱{Number(currentPrice).toFixed(2)}
                      </span>

                      <span className="text-lg line-through text-gray-400">
                        ₱{Number(originalPrice).toFixed(2)}
                      </span>
                    </>
                  ) : (
                    <span className="text-3xl font-bold">
                      ₱{Number(currentPrice).toFixed(2)}
                    </span>
                  )}
                </div>

                {hasDiscount && (
                  <div className="flex gap-4 text-sm">
                    <div>
                      <span className="bg-red-100 text-red-700 px-2 py-1 rounded font-bold">
                        {discountPercentage}% OFF
                      </span>
                    </div>

                    <div className="text-green-600 font-medium">
                      Save ₱{Number(savings).toFixed(2)}
                    </div>
                  </div>
                )}
              </div>

              {/* =================================================
                  STOCK / HOLD
              ================================================= */}

              {stockAvailable !== null && (
                <div>
                  {isProductHold ? (
                    <p className="text-sm text-red-600 font-medium">
                      ● Product on hold
                    </p>
                  ) : isOutOfStock ? (
                    <p className="text-sm text-red-600 font-medium">
                      ● Out of Stock
                    </p>
                  ) : (
                    <p className="text-sm text-green-600 font-medium">
                      ●{" "}
                      {stockAvailable <= 5
                        ? `Only ${stockAvailable} left`
                        : `In Stock (${stockAvailable} available)`}
                    </p>
                  )}
                </div>
              )}

              {/* =================================================
                  PREPARATION TIME
              ================================================= */}

              {product.preparationTime !== undefined &&
                product.preparationTime !== null && (
                  <div className="text-sm text-gray-600">
                    <Clock className="w-4 h-4 inline mr-2" />
                    Preparation time:{" "}
                    <span className="font-medium">
                      {Number(product.preparationTime) || 0} minutes
                    </span>
                  </div>
                )}

              {/* =================================================
                  QUANTITY
              ================================================= */}

              <div className="border-t pt-3">
                <label
                  htmlFor="product-quantity"
                  className="text-sm font-medium block mb-2"
                >
                  Quantity
                </label>

                <div className="flex items-center border rounded-lg w-fit overflow-hidden">
                  <button
                    type="button"
                    onClick={handleDecreaseQuantity}
                    disabled={quantity <= 1 || isAddingToCart || isUnavailable}
                    className="px-4 py-2 hover:bg-gray-100 disabled:opacity-50"
                    aria-label="Decrease quantity"
                  >
                    <Minus className="w-4 h-4" />
                  </button>

                  <input
                    id="product-quantity"
                    type="number"
                    value={quantity}
                    onChange={handleQuantityInput}
                    min="1"
                    max={maxQuantity}
                    disabled={isAddingToCart || isUnavailable}
                    aria-label="Quantity"
                    className="w-16 text-center py-2 border-none focus:outline-none"
                  />

                  <button
                    type="button"
                    onClick={handleIncreaseQuantity}
                    disabled={
                      quantity >= maxQuantity || isAddingToCart || isUnavailable
                    }
                    className="px-4 py-2 hover:bg-gray-100 disabled:opacity-50"
                    aria-label="Increase quantity"
                  >
                    <Plus className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {/* =================================================
                  ADD TO CART
              ================================================= */}

              <Button
                onClick={handleAddToCart}
                disabled={
                  isUnavailable ||
                  isAddingToCart ||
                  (variants.length > 0 && !effectiveVariant) ||
                  !isPriceValid
                }
                className="w-full gap-2 rounded-lg bg-red-600 text-white hover:bg-red-700 focus-visible:ring-red-600"
              >
                <ShoppingCart className="w-5 h-5" />

                {storeClosed ? "Store closed" : isAddingToCart
                  ? "Adding..."
                  : isProductHold
                  ? "On hold"
                  : isOutOfStock
                  ? "Out of Stock"
                  : variants.length > 0 && !effectiveVariant
                  ? "Select Variant"
                  : !isPriceValid
                  ? "Price Unavailable"
                  : "Add to Cart"}
              </Button>

              {/* =================================================
                  SALES
              ================================================= */}

              {product.totalSold !== undefined &&
                Number(product.totalSold) > 0 && (
                  <div className="text-xs text-gray-500 text-center bg-green-50 p-2 rounded">
                    <strong>{product.totalSold}+</strong> sold and satisfied
                    customers
                  </div>
                )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* ========================================================
          DESCRIPTION
      ======================================================== */}

      {variantDescription && (
        <div className="bg-white rounded-lg p-3 border">
          <h3 className="text-lg font-semibold mb-3">{product.name}{effectiveVariant?.variantName ? ` - ${effectiveVariant.variantName}` : ''}</h3>

          <p className="text-gray-700 leading-relaxed whitespace-pre-wrap">
            {variantDescription}
          </p>
        </div>
      )}

      {/* ========================================================
          ADD-ON INFORMATION
      ======================================================== */}

      {product.isAddOn && (
        <Card className="border-blue-200 bg-blue-50">
          <CardContent className="p-3">
            <p className="text-sm text-blue-700">
              This product can be added as an add-on to other items in your
              order.
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default ProductDetail;
